import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftRight, Check, Plus, Search, X } from 'lucide-react';

import { trackEvent } from '../../utils/gt';
import { EXERCISE_REGIONS, regionOf } from '../../utils/exerciseRegions';
import { ExerciseArt, ExerciseWhat } from './parts';
import { libraryTitle, searchLibrary } from './exerciseUtils';

const PAGE_SIZE = 60;

/**
 * The exercise library while editing: search and body parts, a ✓ on what's already in the workout. "add" keeps it
 * open to add several; "swap" replaces the open exercise and closes. The left pane on desktop (where the list was);
 * a full page on phones.
 */
export const ExerciseLibraryPane = ({ mode, letter, inWorkout, addedCount, total, exercises, isLoading, isDesktop, onPick, onAddTyped, onClose }) => {
  const { t, i18n: { language } } = useTranslation();
  const isPt = language === 'pt';
  const lib = (key, options) => t(`pages.Workout.library.${key}`, options);
  const isSwap = mode === 'swap';
  const rootRef = useRef(null);

  const [query, setQuery] = useState('');
  const [region, setRegion] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const results = useMemo(() => searchLibrary(exercises, { query, region, regionOf, isPt }), [exercises, query, region, isPt]);
  const available = useMemo(() => searchLibrary(exercises, { isPt }).length, [exercises, isPt]);

  useEffect(() => setVisibleCount(PAGE_SIZE), [query, region]);

  // One event per settled search, not one per keystroke
  useEffect(() => {
    if (!query.trim()) return undefined;
    const timer = setTimeout(() => trackEvent('exercise_picker_search', `Exercise Picker - Search: ${query.trim()}`, 'exercise_picker', true), 400);
    return () => clearTimeout(timer);
  }, [query]);

  // Phones: a page over the editor that holds the focus; Escape closes it anywhere
  useEffect(() => {
    if (!isDesktop) rootRef.current?.focus({ preventScroll: true });
    const onKey = (event) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isDesktop, onClose]);

  const pick = (attrs) => {
    trackEvent('exercise_picker_select', `Exercise Picker - Select: ${attrs.title}`, 'exercise_picker', true);
    onPick(attrs);
  };

  const status = isSwap
    ? lib('swapHint')
    : addedCount
      ? lib('added', { count: addedCount })
      : lib('inWorkout', { letter, count: total });

  const content = (
    <>
      <div className="mt-tr-lib__head">
        <div className="mt-tr-lib__top">
          <h2 id="mt-tr-lib-title" className="mt-tr-lib__title">
            {isSwap ? lib('swapTitle') : lib('title')}
            {!isLoading && <small>{available}</small>}
          </h2>
          <button type="button" className="mt-tr-icon-btn mt-tr-icon-btn--lg" onClick={onClose} aria-label={lib('close')}>
            <X aria-hidden="true" />
          </button>
        </div>
        <label className="mt-tr-search">
          <Search aria-hidden="true" />
          <span className="mt-sr-only">{lib('search')}</span>
          <input type="search" value={query} placeholder={lib('search')} autoComplete="off" onChange={(event) => setQuery(event.target.value)} />
          {query && (
            <button type="button" className="mt-tr-search__clear" onClick={() => setQuery('')} aria-label={lib('clearSearch')}>
              <X aria-hidden="true" />
            </button>
          )}
        </label>
        {/* Phones swipe the body parts; desktop shows them all, wrapping (owner rule: no sideways scroll there) */}
        <div className="mt-tr-chips" role="group" aria-label={lib('regions')}>
          {[{ key: '' }, ...EXERCISE_REGIONS].map(({ key }) => (
            <button key={key || 'all'} type="button" className="mt-tr-chip" aria-pressed={region === key} onClick={() => setRegion(key)}>
              {key ? lib(`regionNames.${key}`) : lib('all')}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-tr-lib__list">
        {isLoading ? (
          <ul className="mt-tr-lrows" aria-busy="true">
            {Array.from({ length: 8 }, (_, index) => (
              <li key={index} className="mt-tr-lrow mt-tr-lrow--skeleton" />
            ))}
          </ul>
        ) : results.length === 0 ? (
          <div className="mt-tr-lib__empty">
            <p>{lib('empty')}</p>
            {/* Not in the library: it can still go in by name, as before (no video or steps) */}
            {!isSwap && query.trim() && (
              <button type="button" className="mt-tr-btn mt-tr-btn--quiet mt-tr-btn--xs" onClick={() => onAddTyped(query.trim())}>
                <Plus aria-hidden="true" />
                {lib('addTyped', { name: query.trim() })}
              </button>
            )}
          </div>
        ) : (
          <>
            <ul className="mt-tr-lrows">
              {results.slice(0, visibleCount).map(({ id, attributes: attrs }) => {
                const title = libraryTitle(attrs, isPt);
                const isIn = !isSwap && inWorkout.has(attrs.exerciseId);
                return (
                  <li key={id} className="mt-tr-lrow">
                    <ExerciseArt src={attrs.thumbnailUrl} />
                    <span className="mt-tr-lrow__text">
                      <span className="mt-tr-lrow__name">{title}</span>
                      <ExerciseWhat muscle={attrs.muscleGroup} equipment={attrs.equipmentCategory} className="mt-tr-lrow__what" />
                    </span>
                    <button
                      type="button"
                      className={`mt-tr-icon-btn${isIn ? ' is-added' : ''}`}
                      onClick={() => pick(attrs)}
                      aria-label={isSwap ? lib('use', { name: title }) : isIn ? lib('addAgain', { name: title }) : lib('add', { name: title })}
                    >
                      {isSwap ? <ArrowLeftRight aria-hidden="true" /> : isIn ? <Check aria-hidden="true" /> : <Plus aria-hidden="true" />}
                    </button>
                  </li>
                );
              })}
            </ul>
            {visibleCount < results.length && (
              <div className="mt-tr-lib__more">
                <button type="button" className="mt-tr-btn mt-tr-btn--quiet mt-tr-btn--xs" onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}>
                  {lib('more')}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <div className="mt-tr-lib__foot">
        <span aria-live="polite">{status}</span>
        <button type="button" className="mt-tr-btn mt-tr-btn--go mt-tr-btn--sm" onClick={onClose}>
          {lib('done')}
        </button>
      </div>
    </>
  );

  return isDesktop ? (
    <section className="mt-tr-pane mt-tr-lib" aria-labelledby="mt-tr-lib-title" ref={rootRef}>
      {content}
    </section>
  ) : (
    <div className="mt-root mt-tr-lib mt-tr-lib--page" role="dialog" aria-modal="true" aria-labelledby="mt-tr-lib-title" tabIndex={-1} ref={rootRef}>
      {content}
    </div>
  );
};
